/**
 * Prerender state inlining.
 *
 * During the prerender build, a route's component can stash the data it rendered
 * with (e.g. the homepage's regions + featured tours) into an inert
 * `<script type="application/json">` blob via writePrerenderState(). The
 * prerenderer captures that blob into the static HTML.
 *
 * On the client, readPrerenderState() parses that blob synchronously so a
 * component can seed its initial data() to exactly match the prerendered DOM —
 * avoiding the blank-then-fill flash that happens when the SPA re-renders (it
 * does not hydrate) and replaces the prerendered markup.
 *
 * The blob is `type="application/json"` (never executed) and we escape `<` so a
 * value can never break out of the script tag. No CSP concerns either way.
 */

const TAG_ID = '__renedian_prerender_state__'

/** Write the given state object into the inline JSON blob (prerender build only). */
export function writePrerenderState(state) {
  if (typeof document === 'undefined') return
  let json
  try {
    json = JSON.stringify(state == null ? {} : state).replace(/</g, '\\u003c')
  } catch (e) {
    return
  }
  let el = document.getElementById(TAG_ID)
  if (!el) {
    el = document.createElement('script')
    el.id = TAG_ID
    el.type = 'application/json'
    document.body.appendChild(el)
  }
  el.textContent = json
}

let cached

/** Read the inlined prerender state on the client. Returns {} when absent. */
export function readPrerenderState() {
  if (cached !== undefined) return cached
  try {
    const el = typeof document !== 'undefined' ? document.getElementById(TAG_ID) : null
    cached = el && el.textContent ? JSON.parse(el.textContent) : {}
  } catch (e) {
    cached = {}
  }
  return cached
}
